import { useEffect, useState, type ReactNode } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";

// A company document as the customer sees it — the same look as its PDF:
// logo (or name) and contact on top, the title, "## headings", "- bullets",
// and a signature area. In a template's preview, [blanks] to fill in and
// {{fields}} that fill themselves are marked so nothing ships half-done.

export function useCompanyLogo(): string | null {
  const { tenantId } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      doc(db, paths.tenant(tenantId)),
      (s) => setUrl((s.get("logoUrl") as string | undefined) || null),
      () => setUrl(null),
    );
  }, [tenantId]);
  return url;
}

function inline(text: string, mark: boolean): ReactNode[] {
  if (!mark) return [text];
  return text.split(/(\[[^\]\n]+\]|\{\{[^}\n]+\}\})/g).map((part, i) =>
    /^\[[^\]]+\]$/.test(part) ? (
      <mark key={i} className="rounded bg-brandorange/15 px-0.5 text-brandorange-dark" title="Fill this in">
        {part}
      </mark>
    ) : /^\{\{[^}]+\}\}$/.test(part) ? (
      <span key={i} className="rounded bg-steel/10 px-1 font-mono text-[12px] text-steel" title="Fills in from the customer, job or company">
        {part.slice(2, -2)}
      </span>
    ) : (
      part
    ),
  );
}

type Block = { kind: "h"; text: string } | { kind: "ul"; items: string[] } | { kind: "p"; text: string } | { kind: "notice"; text: string };
export function docBlocks(body: string): Block[] {
  const out: Block[] = [];
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      out.push({ kind: "p", text: "" });
      continue;
    }
    if (/^!!\s+/.test(line)) out.push({ kind: "notice", text: line.replace(/^!!\s+/, "") });
    else if (/^##\s+/.test(line)) out.push({ kind: "h", text: line.replace(/^##\s+/, "") });
    else if (/^[-•]\s+/.test(line)) {
      const last = out[out.length - 1];
      const item = line.replace(/^[-•]\s+/, "");
      if (last?.kind === "ul") last.items.push(item);
      else out.push({ kind: "ul", items: [item] });
    } else out.push({ kind: "p", text: line });
  }
  return out.filter((b, i, a) => !(b.kind === "p" && !b.text && (i === 0 || (a[i - 1]?.kind === "p" && !(a[i - 1] as { text: string }).text))));
}

export function DocView({
  title,
  body,
  companyName,
  companyContact = [],
  logoUrl,
  dateLabel,
  requiresSignature,
  markBlanks = false,
  signer = "customer",
  className,
}: {
  title: string;
  body: string;
  companyName: string;
  companyContact?: string[];
  logoUrl: string | null;
  dateLabel?: string;
  requiresSignature: boolean;
  /** Template previews: mark [blanks] and {{fields}}. */
  markBlanks?: boolean;
  /** "company": signed for the company ("By" / "Its"), as lien documents are. */
  signer?: "customer" | "company";
  className?: string;
}) {
  // A statutory notice must be the largest type: nothing else outsizes it.
  const hasNotice = /^!!\s/m.test(body);
  return (
    <div className={cn("rounded-lg bg-white p-5 text-[14px] leading-relaxed text-ink shadow-sm ring-1 ring-steel/15 sm:p-9", className)}>
      <div className="flex items-start justify-between gap-4">
        {logoUrl ? (
          <img src={logoUrl} alt={companyName} className="max-h-14 max-w-[180px] object-contain" />
        ) : (
          <div className={cn("font-bold text-ink", hasNotice ? "text-base" : "text-xl")}>{companyName || "Your company"}</div>
        )}
        <div className="text-right text-xs text-steel">
          {logoUrl ? <div className="text-sm font-semibold text-ink">{companyName}</div> : null}
          {companyContact.filter(Boolean).map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
      </div>
      <div className="mb-5 mt-3 border-b-2 border-brandorange" />
      <h1 className={cn("font-bold leading-tight text-ink", hasNotice ? "text-base" : "text-2xl")}>{title}</h1>
      {dateLabel ? <p className="mb-4 mt-1 text-xs text-steel">{dateLabel}</p> : <div className="mb-4" />}
      {docBlocks(body)
        .filter((b) => !(signer === "company" && b.kind === "notice"))
        .map((b, i) =>
        b.kind === "notice" ? (
          <p key={i} className="my-3 border border-ink p-2 text-base font-bold leading-snug text-ink">
            {b.text}
          </p>
        ) : b.kind === "h" ? (
          <h2 key={i} className="mb-2 mt-5 border-b border-steel/15 pb-1 text-[15px] font-semibold text-ink">
            {inline(b.text, markBlanks)}
          </h2>
        ) : b.kind === "ul" ? (
          <ul key={i} className="mb-2 space-y-1">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2">
                <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-brandorange" />
                <span>{inline(it, markBlanks)}</span>
              </li>
            ))}
          </ul>
        ) : b.text ? (
          <p key={i} className="mb-2">
            {inline(b.text, markBlanks)}
          </p>
        ) : (
          <div key={i} className="h-1" />
        ),
      )}
      {requiresSignature && signer === "company" ? (
        <div className="mt-6 grid max-w-sm gap-y-6 text-[13px]">
          <div className="flex items-end gap-2">
            <span className="font-semibold">By:</span>
            <span className="flex-1 border-b border-steel/60" />
          </div>
          <div className="flex items-end gap-2">
            <span className="font-semibold">Its:</span>
            <span className="flex-1 border-b border-steel/60" />
          </div>
        </div>
      ) : null}
      {/* On a company-signed form the statutory notice follows "By" / "Its". */}
      {signer === "company"
        ? docBlocks(body)
            .filter((b) => b.kind === "notice")
            .map((b, i) => (
              <p key={`n${i}`} className="mt-5 border border-ink p-2 text-base font-bold leading-snug text-ink">
                {(b as { text: string }).text}
              </p>
            ))
        : null}
      {requiresSignature && signer !== "company" ? (
        <div className="mt-8">
          <p className="mb-6 text-[10px] font-semibold uppercase tracking-wide text-steel">Accepted and agreed</p>
          <div className="grid grid-cols-[1fr_auto] gap-x-8 gap-y-6 text-[11px] text-steel">
            <div className="border-t border-steel/60 pt-1">Signature</div>
            <div className="w-32 border-t border-steel/60 pt-1">Date</div>
            <div className="border-t border-steel/60 pt-1">Printed name</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** "[Fill this]" blanks still left in a body. */
export const blanksIn = (body: string) => [...new Set(body.match(/\[[^\]\n]+\]/g) ?? [])];
